// Lead-capture analytics for the site's email-capture forms — HAP-3876.
//
// Every one of these must be called on the *success* path only. Mango controllers answer
// 200 with { success:false, error } on a rejected signup, so an `res.ok` check alone is not
// proof the lead was accepted — parse the body and check `success` before calling this.
// See helpers/adsConversion.js for the post-payment purchase equivalent.

// Google Ads conversion label per form. This is an ALLOW-LIST, deliberately with no
// fallback: a form that is not listed here fires its GA4 events and its dataLayer push but
// NO Ads conversion at all.
//
// It used to fall back to the Newsletter Signup label, which meant all 12 unmapped forms
// reported to Ads as "Newsletter Signup" and could not be told apart (board call, HAP-3921
// — "better to measure nothing than to measure it wrong"). Adding a form to Ads is now a
// two-step job on purpose: the board creates a dedicated conversion action, then its label
// lands here. Until then that form is invisible to Ads by design, not by accident.
const ADS_SEND_TO_BY_FORM = {
	// "Newsletter Signup", action id 7713799641. The global nav subscribe form only —
	// this is the one form the label actually describes.
	global_nav_newsletter: 'AW-11095419204/bUgFCNn7nN4cEMTS2qop',
	// "Worship Together Kit Signup", action id 7715280640.
	wt_kit_signup: 'AW-11095419204/SCgSCICu994cEMTS2qop',
}

// GA4 event name per form, where the requested name differs from `formName`. Anything not
// listed here reports under its own `formName`, which is already stable and unique.
const GA4_EVENT_BY_FORM = {
	global_nav_newsletter: 'newsletter_signup',
}

// Fires GA4's recommended `generate_lead` event, a per-form event, and — only for a form
// listed in ADS_SEND_TO_BY_FORM — a Google Ads conversion.
// `formName` is the only way these 14 forms are told apart in reporting, so it must be
// stable and unique — lowercase with underscores.
export function fireLeadConversion({ formName, campaign, sourceElement } = {}) {
	if (!formName || typeof window.gtag !== 'function') return

	// No `value`/`currency`. A flat $5 per lead was reported here originally as a nominal
	// GA4 lead value, but it surfaced in Ads as real revenue and would have fed a
	// value-based bidding strategy a made-up number. The board assigns lead value
	// account-side instead (HAP-3921).
	const params = {
		event_category: 'engagement',
		form_name: formName,
	}
	if (campaign) params.campaign = campaign
	if (sourceElement) params.source_element = sourceElement

	window.gtag('event', 'generate_lead', params)

	// A per-form event name so a single form can be picked out in GA4 and used as a key
	// event without having to build a `generate_lead` + form_name segment first, and a
	// generic `form_success` so a report can span every form at once. GA4's automatic
	// form_submit cannot see these AJAX submits at all (2 hits vs 287 form_starts, HAP-3921).
	window.gtag('event', GA4_EVENT_BY_FORM[formName] || formName, params)
	window.gtag('event', 'form_success', { event_category: 'engagement', form_id: formName })

	// Allow-list only — an unmapped form reports to GA4 above but sends Ads nothing.
	const adsSendTo = ADS_SEND_TO_BY_FORM[formName]
	if (adsSendTo) window.gtag('event', 'conversion', { send_to: adsSendTo })

	// gtag() does reach window.dataLayer (index.html defines it as a dataLayer.push of its
	// `arguments`), but that arrives as an array-like, which a GTM Custom Event trigger
	// cannot match on. This plain push is what such a trigger would key off. Inert today —
	// container GTM-5P3W4NG holds zero tags (HAP-3917) — so if a tag is ever added here it
	// must not duplicate the gtag calls above.
	window.dataLayer = window.dataLayer || []
	window.dataLayer.push({
		event: GA4_EVENT_BY_FORM[formName] || formName,
		form_name: formName,
		campaign: campaign || undefined,
	})
}

// Counterpart for the failure path, so a drop in leads can be told apart from a broken form.
// Accepts `formId` as an alias so the non-lead forms below can use one vocabulary; both
// params are always sent, which lets a single GA4 report span lead and non-lead forms.
export function fireFormError({ formName, formId } = {}) {
	const id = formName || formId
	if (!id || typeof window.gtag !== 'function') return
	window.gtag('event', 'form_error', { event_category: 'engagement', form_name: id, form_id: id })
}

// GA4-only success signal for the forms that are NOT list signups — inquiries, support
// requests and account admin (HAP-3922). Deliberately fires NO Google Ads conversion and no
// `generate_lead`: folding "I emailed support" or "I logged in" into the Ads lead action
// would pollute the automated bidding signal that HAP-3876 exists to feed. Same
// success-detection rule applies — Mango answers 200 with { success:false } on rejection, so
// call this only after reading the body, never off `res.ok`.
export function fireFormSuccess({ formId } = {}) {
	if (!formId || typeof window.gtag !== 'function') return

	window.gtag('event', 'form_success', { event_category: 'engagement', form_id: formId })

	// Plain push for a GTM Custom Event trigger, which cannot match the `arguments`
	// array-like that gtag() pushes. Inert today — container GTM-5P3W4NG holds zero tags.
	window.dataLayer = window.dataLayer || []
	window.dataLayer.push({ event: 'form_success', form_id: formId })
}
